import { IBreadcrumb, ILocaleValue } from "@types";
import { SLUGS } from "consts";
import { getPublicLocale, Locale } from "consts/locale";

import { getCmsLocale } from "./locale";
import { getTranslatedHref } from "./link";
import { getOfficeAncestorsOldestFirst } from "./officePath";

type TranslationFn = (key: string) => string;

type BreadcrumbData = {
  titles: {
    page: ILocaleValue[];
    shortTitle: string;
    parent?: ILocaleValue[];
    overview?: ILocaleValue[];
  };
  slugs: {
    page: ILocaleValue[];
    parent?: ILocaleValue[];
    overview?: ILocaleValue[];
  };
};

const getBreadcrumbData = (fields: any): BreadcrumbData => ({
  titles: {
    page: fields?.titles || [],
    shortTitle: fields?.shortTitle || "",
    ...(fields?.parent && {
      parent: fields.parent.titles,
    }),
    ...(fields?.overview && { overview: fields.overview.titles }),
  },
  slugs: {
    page: fields?.slugs || [],
    ...(fields?.parent && { parent: fields.parent.slugs }),
    ...(fields?.overview && { overview: fields.overview.slugs }),
  },
});

export const buildBreadcrumbs = ({
  fields,
  locale,
  pathname,
  t,
}: {
  fields: any;
  locale: Locale;
  pathname: string;
  t: TranslationFn;
}): IBreadcrumb[] => {
  if (!fields) return [];
  const { titles, slugs } = getBreadcrumbData(fields);
  const { overview: overviewTitle, parent: parentTitle, page: pageTitle, shortTitle } = titles;
  const { overview: overviewSlug, parent: parentSlug, page: pageSlug } = slugs;

  const officePath = SLUGS.office[locale as keyof typeof SLUGS.office];
  const isOfficePage = pathname?.includes(SLUGS.offices) || pathname?.includes(officePath);
  const servicesPath = SLUGS.servicesPath[locale as keyof typeof SLUGS.servicesPath];
  const isServicePage = pathname?.includes(SLUGS.services) || pathname?.includes(servicesPath);
  const isJobsPage = pathname?.includes(SLUGS.jobs);
  const isForWhoPage = pathname?.includes(SLUGS.forWho[locale as keyof typeof SLUGS.forWho]);
  const isJobsContactPage =
    pathname?.includes(SLUGS.contact) && pathname?.includes(SLUGS.contactJobs);
  const isNewsArticlePage = fields?.__typename === "ArticleRecord";

  const crumbs: IBreadcrumb[] = [];
  let href = `/${getPublicLocale(locale)}`;
  const toLocalizedStaticPath = (path: string) => getTranslatedHref({ href: path }, locale);

  if (isJobsContactPage) {
    crumbs.push({
      href: toLocalizedStaticPath(SLUGS.career),
      label: t("jobs"),
    });
  } else if (isServicePage) {
    crumbs.push({
      href: toLocalizedStaticPath(SLUGS.services),
      label: t("services-overview"),
    });
  }
  if (isOfficePage) {
    crumbs.push({
      href: toLocalizedStaticPath(SLUGS.nearby),
      label: t("nearby"),
    });
  }
  if (isForWhoPage) {
    crumbs.push({
      href: toLocalizedStaticPath(SLUGS.targetGroups),
      label: t("for-who-overview"),
    });
  }
  if (isJobsPage) {
    crumbs.push({
      href: toLocalizedStaticPath(SLUGS.jobs),
      label: t("jobs-overview"),
    });
  }
  if (isNewsArticlePage) {
    crumbs.push({
      href: toLocalizedStaticPath(SLUGS.articles),
      label: t("news"),
    });
  }

  if (overviewTitle && overviewSlug) {
    const title = overviewTitle.find((titleItem) => titleItem.locale === getCmsLocale(locale));
    const slug = overviewSlug.find((s) => s.locale === getCmsLocale(locale));

    href += `/${slug?.value}`;

    if (title && slug) {
      crumbs.push({
        href: href,
        label: title.value,
      });
    }
  }

  if (isOfficePage && fields?.parent) {
    const ancestors = getOfficeAncestorsOldestFirst(fields.parent);
    const cmsLocale = getCmsLocale(locale);
    const officeBase = SLUGS.office[locale as keyof typeof SLUGS.office];

    if (ancestors.length > 0) {
      const first = ancestors[0];
      const firstSlug =
        first.slugs?.find((s: ILocaleValue) => s.locale === cmsLocale)?.value ?? first.slug;
      if (firstSlug) {
        href += `${officeBase}/${firstSlug}`;
        const firstTitle = first.titles?.find((t: ILocaleValue) => t.locale === cmsLocale)?.value;
        if (firstTitle) {
          crumbs.push({
            href,
            label: firstTitle,
          });
        }
      }
      for (let i = 1; i < ancestors.length; i++) {
        const ancestor = ancestors[i];
        const slug =
          ancestor.slugs?.find((s: ILocaleValue) => s.locale === cmsLocale)?.value ?? ancestor.slug;
        const title = ancestor.titles?.find((t: ILocaleValue) => t.locale === cmsLocale)?.value;
        if (slug) {
          href += `/${slug}`;
          if (title) {
            crumbs.push({
              href,
              label: title,
            });
          }
        }
      }
    }
  } else if (parentTitle && parentSlug) {
    const title = parentTitle.find((titleItem) => titleItem.locale === getCmsLocale(locale));
    const slug = parentSlug.find((s) => s.locale === getCmsLocale(locale));

    if (isServicePage) {
      href += `${servicesPath}/${slug?.value}`;
    }

    if (isForWhoPage) {
      href += `${SLUGS.forWho[locale as keyof typeof SLUGS.forWho]}/${slug?.value}`;
    }

    if (title && slug) {
      crumbs.push({
        href,
        label: title.value,
      });
    }
  }

  if (pageTitle && pageSlug) {
    const title = pageTitle.find((titleItem) => titleItem.locale === getCmsLocale(locale));
    const slug = pageSlug.find((s) => s.locale === getCmsLocale(locale));
    href += `/${slug?.value}`;

    if (title && slug) {
      crumbs.push({
        href,
        label: shortTitle || title.value,
      });
    }
  }

  return crumbs;
};
